// Cities are CMS-managed: one JSON file per city in ./cities/ (editable via
// Sveltia CMS at /admin), loaded here at build time and sorted by `order`.
// Photos are produced by scripts/photo-pipeline/ (resize + GPS/EXIF strip) —
// the CMS edits metadata/captions/order; photo *files* stay pipeline-managed.

export interface Photo {
  id: string;
  url: string;
  caption: string;
  width: number;
  height: number;
}

export interface Memory {
  id: string;
  name: string;
  country: string;
  lat: number;
  lng: number;
  date: string;
  blurb: string;
  photos: Photo[];
}

interface CityFile extends Memory {
  order: number;
}

const modules = import.meta.glob<{ default: CityFile }>('./cities/*.json', { eager: true });

// CityFile extends Memory, so the extra `order` field is harmless to consumers.
export const LOCATIONS: Memory[] = Object.values(modules)
  .map((mod) => mod.default)
  .sort((a, b) => a.order - b.order);

export const PHOTO_COUNT = LOCATIONS.reduce((n, c) => n + c.photos.length, 0);

export const PLACES_BY_COUNTRY = LOCATIONS.reduce<Map<string, Memory[]>>(
  (groups, city) => groups.set(city.country, [...(groups.get(city.country) ?? []), city]),
  new Map(),
);

export const COUNTRY_COUNT = PLACES_BY_COUNTRY.size;

export const YEAR_RANGE = (() => {
  const years = LOCATIONS.flatMap((c) => c.date.match(/\d{4}/g) ?? []).map(Number);
  return years.length ? `${Math.min(...years)}–${Math.max(...years)}` : '';
})();

const TIERS = [640, 960];

const tierUrl = (p: Photo, width: number) => p.url.replace(/(\.\w+)$/, `-${width}$1`);

export const photoSrcSet = (p: Photo): string =>
  [...TIERS.filter((w) => w < p.width).map((w) => `${tierUrl(p, w)} ${w}w`), `${p.url} ${p.width}w`].join(', ');

export interface Thumbnail {
  src: string;
  width: number;
  height: number;
}

export const thumbnail = (p: Photo): Thumbnail =>
  p.width > TIERS[0]
    ? { src: tierUrl(p, TIERS[0]), width: TIERS[0], height: Math.round((p.height * TIERS[0]) / p.width) }
    : { src: p.url, width: p.width, height: p.height };

export const getCity = (slug?: string): Memory | undefined =>
  LOCATIONS.find((c) => c.id === slug);
